<template>
    <div class="bg-light-white ">
        <GoBackButtonComponent :path="'/login'" />
    </div>
    
    <div class="h-screen bg-light-white flex flex-col items-center">
        <section class="m-auto px-2 md:px-0 text-secondary" :class="classToUse">
            <LoadingDialog :visible="loading" />
            
            <div class="rounded shadow p-3 text-center bg-white pb-10">
                <LogoComponent :customClass="'text-4xl md:text-6xl'" />
                
                <p class="text-2xl font-bold py-4">
                    {{ translate('Enter the code you received in your email') }}
                </p>
                
                <p class="text-gray-600" v-if="userEmail">
                    {{ translate('We sent a confirmation code to') }}:<br>
                    <strong class="text-secondary">{{ userEmail }}</strong>
                </p>

                <div class="flex justify-center my-8">
                    <InputOtp 
                        v-model="verificationCode" 
                        :length="6"
                        size="large"
                    />
                </div>

                <Button 
                    class="mt-5 w-full bg-purple-suit-forced text-white p-1"
                    :label="translate('verify email', 'capitalize')"
                    @click.prevent="submitVerify()"
                    :loading="loading"
                    :disabled="!isVerificationCodeValid()"
                />

                <Button class="mt-5 w-full no-bg-button border text-white p-1" v-if="canSendEmail()" @click="openSendEmailDialog()">
                    <p class="text-secondary ml-2">
                        {{ translate('resend code', 'capitalize') }}
                    </p>
                </Button>
                <p v-else>
                    {{ translate('You must wait') }}
                </p>

                <p class="mt-8 fill-text-line text-secondary opacity-50 text-xs">
                    {{ translate('did not receive your code? Check your spam.', 'ucfirst') }}
                </p>

                <p class="mt-5 cursor-pointer text-sm font-medium hover:underline transition">
                    <router-link :to="'/'">
                        {{ translate('go back to homepage', 'ucfirst') }}
                    </router-link>
                </p>
            </div>
        </section>

        <LoadingDialog v-if="loading" />

        <Dialog 
            v-model:visible="visibleSendEmailModal" 
            :draggable="false" 
            :closable="false" 
            :header="translate('Send new email confirmation code')" 
            modal 
            :style="{ width: '30rem' }"
        >
            
            <div class="flex flex-col gap-2 mb-8">
                <span class="text-gray-600 block">{{ translate('enter your email', 'ucfirst') }}:</span>
                <InputText 
                    v-model="emailToUseOnSending"
                    class="w-full py-2 px-3 rounded-lg border border-color-iron border-focus-purple-suit"
                    type="text"
                    :placeholder="translate('email', 'ucfirst')"
                    :disabled="sendingEmail"
                />
                <Divider/>
                <span class="text-gray-600 block">{{ translate('enter your password', 'ucfirst') }}:</span>
                <Password 
                    v-model="passwordToUseOnSending" 
                    class="w-full rounded-lg border-focus-purple-suit"
                    :placeholder="translate('your password', 'ucfirst')" 
                    toggleMask fluid
                />
            </div>
            <div class="flex justify-end gap-2">
                <Button type="button" v-if="!sendingEmail" :label="translate('cancel', 'ucfirst')" class="no-bg-button" @click="goToLoginPage()"></Button>
                <Button 
                    :disabled="(!emailToUseOnSending && !passwordToUseOnSending)" 
                    type="button" 
                    :loading="sendingEmail" 
                    :label="translate('send', 'ucfirst')" 
                    class="bg-button" 
                    icon="pi pi-send"
                    @click="trySendingEmail()"
                />
            </div>
        </Dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import Button from 'primevue/button';
import InputOtp from 'primevue/inputotp';
import { translate } from '@/composables/translate';
import { useRoute } from 'vue-router';
import { isScreeSmall } from '@/composables/windowSize';
import cookiesHandler from '@/composables/cookiesHandler';
import DateHandler from '@/composables/handlers/DateHandler';
import LoadingDialog from '@/components/Utils/LoadingDialog.vue';
import UserService from '@/services/UserService';
import { showToast } from '@/composables/toast';
import { useToast } from 'primevue/usetoast';
import { useSessionMessageStore } from '@/stores/sessionMessage';
import urlHandler from '@/composables/urlHandler';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import { Password } from 'primevue';
import GoBackButtonComponent from '@/components/misc/GoBackButtonComponent.vue';
import LogoComponent from '@/components/LogoComponent.vue';

const route = useRoute();
const toast = useToast();
const sessionManager = useSessionMessageStore();

const breakPoint = 500;
const userEmail = ref('');
const loading = ref(false);
const verificationCode = ref(null);
const hasCode = ref(false);

const visibleSendEmailModal = ref(false);
const emailToUseOnSending = ref(null);
const passwordToUseOnSending = ref(null);

onMounted(() => {
    checkParameters();

    window.addEventListener('resize', updateScreenSize);
    updateScreenSize();
});

onUnmounted(() => {
    window.removeEventListener('resize', updateScreenSize);
});

const classToUse = ref('');
const screenSize = ref(window.innerWidth);
const updateScreenSize = () => {
    screenSize.value = window.innerWidth;
    classToUse.value = isScreeSmall(breakPoint) ? 'w-full' : 'm-form-w';
};

const remainingTimeToSendNewEmail = ref(0);
function canSendEmail() {
    const date = cookiesHandler.getEncryptedCookie('TM_VE_SENT');

    if (!date || hasCode.value == false) {
        return true;
    }

    const sentEmailTime = DateHandler.parseDate(date);

    if (sentEmailTime) {
        const seconds = DateHandler.diffInSeconds(sentEmailTime);
        remainingTimeToSendNewEmail.value = seconds - 180;

        return seconds > 360;
    }

    remainingTimeToSendNewEmail.value = 0;
    
    return false;
}

const submitVerify = async () => {
    loading.value = true;

    const response = await UserService.sendEmailVerificationCode(verificationCode.value);

    loading.value = false;

    if (!response.success()) {
        showToast(toast, translate(response.getMessage(), 'ucfirst'), 'error');
        return;
    }
    
    sessionManager.successMessage(translate('email verified', 'success'));
    urlHandler.redirectAndScroll('/login');
};

function isVerificationCodeValid() {
    return verificationCode.value != null && verificationCode.value.length == 6;
}

async function checkParameters() {
    const action = route.query?.action || null;
    const email = route.query?.email || null;

    if (email) {
        emailToUseOnSending.value = email;
    }

    hasCode.value = route.query.hc || false;
    verificationCode.value = route.query.code || '';

    if (isVerificationCodeValid()) {
        loading.value = true;
        await submitVerify();
        loading.value = false;
    }

    if (action == 'resend') {
        showToast(toast, translate('email not verified, confirm your email and password to receive a new email verification code', 'ucfirst'), 'info');
        openSendEmailDialog();
    }
}

function openSendEmailDialog() {
    if (userEmail.value) {
        emailToUseOnSending.value = userEmail.value;
    }

    visibleSendEmailModal.value = true;
}

const sendingEmail = ref(false);
async function trySendingEmail() {
    const email = emailToUseOnSending.value;
    const password = passwordToUseOnSending.value;

    if (!email || !password) {
        showToast(toast, translate('email and password are required', 'ucfirst'), 'error');
        return;
    }

    sendingEmail.value = true;
    const response = await UserService.sendNewVerificationEmail(email, password); 
    sendingEmail.value = false;

    if (!response.success()) {
        showToast(toast, translate(response.getMessage(), 'ucfirst'), 'error');
        showToast(toast, translate('wait', 'ucfirst') + ': ' + response.getContent().timeLeft, 'info');
        return;
    }

    visibleSendEmailModal.value = false;
    showToast(toast, translate('new email sent', 'ucfirst'), 'info');
    cookiesHandler.setEncryptedCookie('TM_VE_SENT', DateHandler.nowDateFormated(), 3);
}

function goToLoginPage() {
    urlHandler.redirectAndScroll('/login');
}

</script>

<style scoped>
.fill-text-line {
    display: flex;
    align-items: center;
    text-align: center;
}
.fill-text-line::before, .fill-text-line::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid rgba(0,0,0,0.1);
}
.fill-text-line:not(:empty)::before { margin-right: .5em; }
.fill-text-line:not(:empty)::after { margin-left: .5em; }
</style>